import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import colors from "../../assets/theme/colors";
import KnowledgeProfileItemGroups from "./KnowledgeProfileItemGroups";
import HandbookSourceListViewer from "./HandbookSourceListViewer";
import useOverflowToggle from "./useOverflowToggle";

const compactText = (value = "", maxLength = 112) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  const sentence = text.match(/^(.{24,112}?[.!?])(?:\s|$)/)?.[1];
  return sentence || `${text.slice(0, maxLength - 1).trim()}…`;
};

const compactDateLabel = (value = "") => {
  const currentYear = new Date().getFullYear();
  return String(value || "").replace(new RegExp(`, ${currentYear}$`), "");
};

const ProfileItem = ({
  entry,
  onEntryAction,
  onEntryVisibilityAction,
  onEntryMarkOutdated,
  onEntryDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const actionButtonRef = useRef(null);
  const pendingActionRef = useRef(null);
  const [confirmation, setConfirmation] = useState(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const entryText = entry.fullText || entry.headline || entry.explanation || "";
  const {
    contentRef,
    hasOverflow: entryHasOverflow,
    expanded,
    toggleExpanded,
  } = useOverflowToggle(entryText);
  const sourceAction = entry.sourceAction;
  const presentation = entry.sourcePresentation;
  const sourceRecords = Array.isArray(entry.provenance?.records) ? entry.provenance.records : [];
  const canViewSupportingSources = sourceRecords.length > 1;
  const isDirect = presentation?.sourceKind === "caregiver_direct";
  const canEdit = sourceAction?.ownership === "caregiver_fact"
    && sourceAction?.isEditable
    && onEntryAction;
  const canViewFullEntry = isDirect && sourceAction?.actionId && onEntryAction;
  const canViewSource = sourceAction?.ownership === "external_record" && onEntryAction;
  const canOpenManager = sourceAction?.ownership === "manager_reference" && onEntryAction;
  const canDelete = isDirect && canEdit && onEntryDelete;
  const hasMenu = Boolean(
    canEdit
    || canViewFullEntry
    || canViewSource
    || canOpenManager
    || canViewSupportingSources
    || (entry.knowledgeFactId && onEntryVisibilityAction)
    || (entry.knowledgeFactId && onEntryMarkOutdated)
    || canDelete
  );

  const closeMenu = () => setAnchorEl(null);
  useEffect(() => {
    if (anchorEl || !pendingActionRef.current) return;
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    actionButtonRef.current?.focus();
    action(actionButtonRef.current);
  }, [anchorEl]);

  const runAction = (action) => {
    pendingActionRef.current = action;
    closeMenu();
  };

  const requestConfirmation = (kind) => {
    closeMenu();
    setConfirmation(kind);
  };

  const confirmationCopy = {
    hide: {
      title: "Hide from Profile?",
      body: "This removes the fact from the current Profile. It may still be true and remains in Knowledge with its source.",
      action: "Hide from Profile",
      run: () => onEntryVisibilityAction(entry),
    },
    outdated: {
      title: "Mark as outdated?",
      body: "Use this when the fact used to be true but is no longer current. Its history and original source will be preserved.",
      action: "Mark as outdated",
      run: () => onEntryMarkOutdated(entry),
    },
    delete: {
      title: "Delete this fact permanently?",
      body: "This permanently deletes the fact added directly through Profile. This cannot be undone.",
      action: "Delete",
      run: () => onEntryDelete(entry),
    },
  }[confirmation];

  const confirmAction = () => {
    confirmationCopy?.run();
    setConfirmation(null);
  };

  return (
    <Box
      sx={{
        py: { xs: 0.8, sm: 0.95 },
      }}
    >
      <Stack direction="row" spacing={0.9} alignItems="flex-start">
        <CheckRoundedIcon
          aria-hidden="true"
          sx={{
            mt: 0.05,
            flexShrink: 0,
            fontSize: 22,
            color: alpha(colors.semantic.sageDeep, 0.7),
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography ref={contentRef} sx={{
            color: colors.semantic.ink2,
            fontSize: { xs: "1rem", sm: "1.05rem" },
            fontWeight: 500,
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: expanded ? 'unset' : 2,
            overflow: "hidden",
          }}>
            {entryText}
          </Typography>
          {(entryHasOverflow || expanded) ? (
            <Button size="small" onClick={toggleExpanded} aria-expanded={expanded} sx={{ p: 0, textTransform: "none" }}>
              {expanded ? "Show less" : "Read more"}
            </Button>
          ) : null}
          {canViewSource && sourceAction.isAvailable !== false ? (
            <Button size="small" onClick={(event) => onEntryAction(entry, event.currentTarget)} sx={{ display: "block", p: 0, textTransform: "none" }}>
              View original entry
            </Button>
          ) : null}
          {presentation?.supportingSourceCount > 1 ? (
            <Typography sx={{
              mt: 0.35,
              color: alpha(colors.semantic.ink2, 0.72),
              fontSize: "0.8rem",
              fontWeight: 500,
              lineHeight: 1.35,
            }}>
              {[
                presentation.originLabel,
                compactDateLabel(presentation.dateLabel),
                `Supported by ${presentation.supportingSourceCount} captures`,
                presentation.latestSourceDateLabel
                  ? `Latest ${compactDateLabel(presentation.latestSourceDateLabel)}`
                  : "",
              ].filter(Boolean).join(" · ")}
            </Typography>
          ) : presentation?.originLabel ? (
            <Typography sx={{
              mt: 0.35,
              color: alpha(colors.semantic.ink2, 0.72),
              fontSize: "0.8rem",
              fontWeight: 500,
              lineHeight: 1.35,
            }}>
              {[presentation.originLabel, compactDateLabel(presentation.dateLabel)].filter(Boolean).join(" · ")}
            </Typography>
          ) : null}
          {presentation?.approvalLabel?<Typography sx={{fontSize:"0.78rem",color:"text.secondary"}}>{presentation.approvalLabel}</Typography>:null}
          {presentation?.sourceId ? <Button component="a" href={`/sources/${presentation.sourceId}`} size="small" sx={{ p: 0, textTransform: "none" }}>Open source note</Button> : null}
          {presentation?.needsReview ? (
            <Typography sx={{
              mt: 0.35,
              color: colors.semantic.amberDeep || colors.semantic.ink2,
              fontSize: "0.76rem",
              fontWeight: 700,
              lineHeight: 1.35,
            }}>
              Needs review
            </Typography>
          ) : null}
          {entryHasOverflow && canViewFullEntry ? (
            <Button
              onClick={(event) => onEntryAction(entry, event.currentTarget, { readOnly: true, fullEntry: true })}
              size="small"
              sx={{
                mt: 0.45,
                p: 0,
                minWidth: 0,
                color: colors.semantic.sageDeep,
                textTransform: "none",
                textDecoration: "underline",
                fontWeight: 500,
                fontSize: "0.8rem",
              }}
            >
              View full entry
            </Button>
          ) : null}
        </Box>
        {hasMenu ? (
          <>
            <IconButton
              ref={actionButtonRef}
              size="small"
              aria-label={`Actions for ${compactText(entry.headline || entry.explanation)}`}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              sx={{ mt: -0.45, mr: -0.5, color: alpha(colors.semantic.ink2, 0.72) }}
            >
              <MoreHorizRoundedIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeMenu}
              slotProps={{ paper: { sx: { minWidth: 190, borderRadius: "14px" } } }}
            >
              {canViewSupportingSources ? (
                <MenuItem onClick={() => runAction(() => setSourcesOpen(true))}>
                  View sources
                </MenuItem>
              ) : null}
              {!canViewSupportingSources && (canViewSource || canOpenManager) ? (
                <MenuItem onClick={() => runAction((trigger) => onEntryAction(entry, trigger))}>
                  {sourceAction.actionLabel || "View source"}
                </MenuItem>
              ) : null}
              {canEdit ? (
                <MenuItem onClick={() => runAction((trigger) => onEntryAction(entry, trigger))}>
                  Edit
                </MenuItem>
              ) : null}
              {entry.knowledgeFactId && onEntryVisibilityAction ? (
                <MenuItem onClick={() => requestConfirmation("hide")}>
                  Hide from Profile
                </MenuItem>
              ) : null}
              {entry.knowledgeFactId && onEntryMarkOutdated ? (
                <MenuItem onClick={() => requestConfirmation("outdated")}>
                  Mark as outdated
                </MenuItem>
              ) : null}
              {canDelete ? (
                <MenuItem onClick={() => requestConfirmation("delete")} sx={{ color: "error.main" }}>
                  Delete
                </MenuItem>
              ) : null}
              {canViewSource ? (
                <Box sx={{ px: 2, py: 1, maxWidth: 240 }}>
                  <Typography sx={{ fontSize: "0.72rem", lineHeight: 1.35, color: alpha(colors.semantic.ink2, 0.64) }}>
                    The original source is preserved and cannot be edited here.
                  </Typography>
                </Box>
              ) : null}
            </Menu>
            <Dialog
              open={Boolean(confirmationCopy)}
              onClose={() => setConfirmation(null)}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle sx={{ fontWeight: 900 }}>{confirmationCopy?.title}</DialogTitle>
              <DialogContent>
                <Typography sx={{ color: alpha(colors.semantic.ink2, 0.82), lineHeight: 1.5 }}>
                  {confirmationCopy?.body}
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={() => setConfirmation(null)} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
                <Button
                  onClick={confirmAction}
                  variant="contained"
                  color={confirmation === "delete" ? "error" : "primary"}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  {confirmationCopy?.action}
                </Button>
              </DialogActions>
            </Dialog>
            <HandbookSourceListViewer
              open={sourcesOpen}
              records={sourceRecords}
              onClose={() => setSourcesOpen(false)}
            />
          </>
        ) : null}
      </Stack>
    </Box>
  );
};

const KnowledgeProfileItemList = ({
  handbook,
  onEntryAction,
  onEntryVisibilityAction,
  onEntryMarkOutdated,
  onEntryDelete,
  onAddCta,
  addCtaLabel,
  showGroupHeadings = false,
}) => {
  return (
    <Stack spacing={1}>
      <KnowledgeProfileItemGroups
        handbook={handbook}
        showGroupHeadings={showGroupHeadings}
        renderEntry={(entry) => (
          <ProfileItem
            key={entry.id}
            entry={entry}
            onEntryAction={onEntryAction}
            onEntryVisibilityAction={onEntryVisibilityAction}
            onEntryMarkOutdated={onEntryMarkOutdated}
            onEntryDelete={onEntryDelete}
          />
        )}
      />
      {onAddCta ? (
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={onAddCta}
          sx={{
            mt: 0.4,
            py: 1,
            borderRadius: "14px",
            bgcolor: colors.semantic.sageDeep,
            boxShadow: "none",
            textTransform: "none",
            fontWeight: 500,
            "&:hover": {
              bgcolor: colors.semantic.sageDeep,
              boxShadow: `0 4px 12px ${alpha(colors.semantic.sageDeep, 0.2)}`,
            },
          }}
        >
          {addCtaLabel || "Add"}
        </Button>
      ) : null}
    </Stack>
  );
};

export default KnowledgeProfileItemList;
